import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { MainToWorkerMessageType, WorkerToMainMessageType } from '@/enum/SignalRWorkerMessageType'
import { logError } from '@/utils/helpers/logger'
import { WORKER_SIGNAL_R } from '@/utils/constants'
import state from './state'
import getters from './getters'

const useWorkersStore = defineStore('workers', {
  state: () => state,
  getters,
  actions: {
    saveWorker(workerId: string, worker: Worker) {
      if (!workerId || !worker) {
        logError('Invalid worker or worker ID')
        return
      }
      this.workers = this.workers || {}
      this.workers[workerId] = markRaw(worker)
    },

    killWorker(workerId: string, callback?: () => void) {
      if (!this.workers?.[workerId]) {
        logError(`No worker found with ID: ${workerId}`)
        return
      }

      const worker: Worker = this.workers[workerId]

      worker.postMessage({ name: MainToWorkerMessageType.Unsubscribe })

      worker.onmessage = ({ data }) => {
        const { name } = data
        switch (name) {
          case WorkerToMainMessageType.Unsubscribed:
            worker.postMessage({ name: MainToWorkerMessageType.Disconnect })
            break

          case WorkerToMainMessageType.Disconnected:
            this.terminateWorker(workerId, callback)
            break

          default:
            logError(`Unhandled worker message: ${name}`)
            break
        }
      }
    },

    terminateWorker(workerId: string, callback?: () => void) {
      const worker: Worker | undefined = this.workers?.[workerId]
      if (worker) {
        worker.terminate()
        delete this.workers?.[workerId]
        console.log(`Worker ${workerId} terminated.`)
        callback?.()
      } else {
        logError(`Worker ${workerId} not found during termination.`)
      }
    },

    setSignalRConsumer(route: string) {
      if (!route || !this.workers?.[WORKER_SIGNAL_R]) {
        logError('Invalid route or SignalR worker not available')
        return
      }
      this.workers[WORKER_SIGNAL_R].postMessage({
        name: MainToWorkerMessageType.UpdateCommunicationCondition,
        route
      })
    }
  }
})

export default useWorkersStore
